import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { strategyApi, StrategyResult, CreateStrategyResultRequest, workflowApi, linkedInProfileApi, LinkedInProfile } from '../services/api';
import { useToast } from '../sectepe-ui/index';
import { PageHeader, useConfirm, EmptyState } from '../components/ui';
import { routeById } from '../navigation/routes';
import { FlowStepBar, useLinkedInFlow } from '../linkedin';
import { Icon } from '../components/icons';
import MarkdownView from '../components/MarkdownView';

/** Zieht Aufzaehlungszeilen (-, *, •, 1.) aus einem KI-Text. */
export function extractListItems(text: string | undefined | null): string[] {
  if (!text) return [];
  const marker = /^(?:[-*\u2022]|\d+[.)])\s+/;
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => marker.test(line))
    .map((line) => line.replace(marker, '').replace(/\*\*/g, '').trim())
    .filter((line) => line.length > 3)
    .slice(0, 20);
}

const parseIds = (json: string | null | undefined): string[] => {
  if (!json) return [];
  try { const v = JSON.parse(json); return Array.isArray(v) ? v : []; } catch { return []; }
};

const StrategyWorkflowPage: React.FC = () => {
  const { push } = useToast();
  const { confirm } = useConfirm();
  const { refresh } = useLinkedInFlow();
  const [profiles, setProfiles] = useState<LinkedInProfile[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [strategies, setStrategies] = useState<StrategyResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Workflow execution state
  const [showWorkflowRunner, setShowWorkflowRunner] = useState(false);
  const [workflowRunning, setWorkflowRunning] = useState(false);
  const [workflowResults, setWorkflowResults] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [workflowInputs, setWorkflowInputs] = useState({
    industry: '',
    target_audience: '',
  });

  const [formData, setFormData] = useState<CreateStrategyResultRequest>({
    name: '',
    targetAudience: '',
    industry: '',
    icp: '',
    challenges: [],
    painPoints: [],
    rawAnalysis: '',
  });

  useEffect(() => {
    loadStrategies();
  }, []);

  const loadStrategies = async () => {
    try {
      setLoading(true);
      const [data, profileList] = await Promise.all([
        strategyApi.getAll(true),
        linkedInProfileApi.getAll().catch(() => [] as LinkedInProfile[]),
      ]);
      setStrategies(data ?? []);
      setProfiles(profileList ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Strategie-Ergebnisse');
    } finally {
      setLoading(false);
    }
  };

  const handleRunWorkflow = async () => {
    if (!workflowInputs.industry || !workflowInputs.target_audience) {
      setError('Bitte Branche und Zielgruppe angeben');
      return;
    }

    setWorkflowRunning(true);
    setWorkflowResults([]);
    setCurrentStep(0);
    setError('');

    try {
      // Find the Phase 1 Strategy workflow
      const workflows = await workflowApi.getWorkflows();
      const strategyWorkflow = workflows.find((w: any) =>
        w.name.includes('Phase 1: Strategie')
      );

      if (!strategyWorkflow) {
        setError('Strategie-Workflow nicht gefunden. Bitte unter Workflows die Schaltflaeche "Vorlagen laden" verwenden.');
        setWorkflowRunning(false);
        return;
      }

      const steps = JSON.parse(strategyWorkflow.steps);
      const variables: Record<string, string> = { ...workflowInputs };
      const results: any[] = [];

      for (let i = 0; i < steps.length; i++) {
        setCurrentStep(i + 1);
        const result = await workflowApi.executeWorkflowStep(strategyWorkflow.id, {
          variables,
          stepIndex: i,
        });
        results.push(result.step);
        variables[`step_${steps[i].id}_result`] = result.step.content;
      }

      setWorkflowResults(results);

      // Pre-fill the form with the results
      setFormData({
        name: `Strategie: ${workflowInputs.target_audience} (${new Date().toLocaleDateString('de-DE')})`,
        targetAudience: workflowInputs.target_audience,
        industry: workflowInputs.industry,
        icp: results[1]?.content || '',
        painPoints: extractListItems(results[2]?.content),
        challenges: [],
        rawAnalysis: results.map(r => `### ${r.stepName}\n\n${r.content}`).join('\n\n---\n\n'),
      });
      setShowForm(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Ausfuehren des Workflows');
    } finally {
      setWorkflowRunning(false);
    }
  };

  const handleSaveStrategy = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      await strategyApi.create(formData);
      await loadStrategies();
      refresh();
      push({ message: 'Strategie gespeichert.', severity: 'success' });
      setShowForm(false);
      setShowWorkflowRunner(false);
      setWorkflowResults([]);
      setFormData({
        name: '',
        targetAudience: '',
        industry: '',
        icp: '',
        challenges: [],
        painPoints: [],
        rawAnalysis: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Strategie löschen?',
      message: 'Beitragsideen, die darauf verweisen, bleiben erhalten.',
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await strategyApi.delete(id);
      await loadStrategies();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Loeschen');
    }
  };

  const handleSetActive = async (id: string) => {
    setBusyId(id);
    setError('');
    try {
      // Das Backend kennt keine Exklusivitaet -- die anderen werden hier deaktiviert.
      await strategyApi.update(id, { isActive: true });
      await Promise.all(
        strategies.filter((s) => s.id !== id && s.isActive).map((s) => strategyApi.update(s.id, { isActive: false })),
      );
      await loadStrategies();
      refresh();
      push({ message: 'Strategie ist jetzt aktiv.', severity: 'success' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Aktivieren');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleProfile = async (strategy: StrategyResult, profileId: string) => {
    const current = parseIds(strategy.linkedInProfileIds);
    const next = current.includes(profileId) ? current.filter((p) => p !== profileId) : [...current, profileId];
    setBusyId(strategy.id);
    try {
      await strategyApi.update(strategy.id, { linkedInProfileIds: next });
      await loadStrategies();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Verknüpfen');
    } finally {
      setBusyId(null);
    }
  };

  const parseJson = (jsonStr: string | null | undefined): string[] => {
    if (!jsonStr) return [];
    try { return JSON.parse(jsonStr); } catch { return []; }
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div><span className="loading-text">Laden...</span></div>;
  }

  return (
    <div className="page-container">
      <FlowStepBar stepId="strategy" />
      <PageHeader
        title="Strategie & Zielkundenprofil (ICP)"
        subtitle={routeById('li-strategy')?.description}
        actions={(
          <button
            onClick={() => setShowWorkflowRunner(!showWorkflowRunner)}
            className="btn btn-primary"
          >
            {showWorkflowRunner ? 'Schliessen' : 'Neue Strategie-Analyse starten'}
          </button>
        )}
      />

      {error && <div className="alert alert-error">{error}</div>}

      {/* Workflow Runner */}
      {showWorkflowRunner && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
          <h2 className="form-section-title">Strategie-Workflow ausfuehren</h2>
          <p style={{ color: 'var(--sec-fg-muted)', fontSize: '14px', marginBottom: '16px' }}>
            Der Workflow fuehrt 3 Schritte aus: 1) Zielgruppen-Recherche (Perplexity), 2) ICP erstellen (ChatGPT), 3) Pain Points priorisieren (Claude).
          </p>

          <div className="form-group">
            <label className="form-label">Branche *</label>
            <input
              type="text"
              value={workflowInputs.industry}
              onChange={(e) => setWorkflowInputs({ ...workflowInputs, industry: e.target.value })}
              placeholder="z.B. IT-Sicherheit, SaaS, Maschinenbau"
              className="form-input"
              disabled={workflowRunning}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Zielgruppe *</label>
            <textarea
              value={workflowInputs.target_audience}
              onChange={(e) => setWorkflowInputs({ ...workflowInputs, target_audience: e.target.value })}
              placeholder="z.B. IT-Leiter und CISOs in mittelstaendischen Unternehmen (500-5000 Mitarbeiter)"
              rows={3}
              className="form-textarea"
              disabled={workflowRunning}
            />
          </div>

          <button
            onClick={handleRunWorkflow}
            disabled={workflowRunning}
            className="btn btn-success"
            style={{ marginBottom: '16px' }}
          >
            {workflowRunning ? `Schritt ${currentStep}/3 wird ausgefuehrt...` : 'Analyse starten'}
          </button>

          {/* Workflow Progress */}
          {(workflowRunning || workflowResults.length > 0) && (
            <div style={{ marginTop: '16px' }}>
              {[
                'Zielgruppen-Recherche (Perplexity)',
                'ICP erstellen (ChatGPT)',
                'Pain Points priorisieren (Claude)',
              ].map((stepName, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '12px 16px',
                    marginBottom: '8px',
                    borderRadius: '8px',
                    backgroundColor:
                      workflowResults[idx] ? 'color-mix(in srgb, var(--sec-success) 12%, var(--sec-surface))' :
                      currentStep === idx + 1 && workflowRunning ? 'color-mix(in srgb, var(--sec-warning) 14%, var(--sec-surface))' :
                      'var(--sec-surface-alt)',
                    border: `1px solid ${
                      workflowResults[idx] ? 'color-mix(in srgb, var(--sec-success) 30%, var(--sec-surface))' :
                      currentStep === idx + 1 && workflowRunning ? 'color-mix(in srgb, var(--sec-warning) 35%, var(--sec-surface))' :
                      'var(--sec-border)'
                    }`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      width: '24px', height: '24px', borderRadius: '50%',
                      backgroundColor: workflowResults[idx] ? 'var(--sec-success)' : currentStep === idx + 1 && workflowRunning ? 'var(--sec-warning)' : 'var(--sec-fg-subtle)',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: 'bold',
                    }}>
                      {workflowResults[idx] ? '\u2713' : idx + 1}
                    </span>
                    <strong style={{ fontSize: '14px' }}>{stepName}</strong>
                    {currentStep === idx + 1 && workflowRunning && (
                      <span style={{ fontSize: '12px', color: 'var(--sec-warning)' }}>Wird ausgefuehrt...</span>
                    )}
                  </div>
                  {workflowResults[idx] && (
                    <div style={{ marginTop: '8px', padding: '12px', backgroundColor: 'white', borderRadius: '4px', maxHeight: '300px', overflow: 'auto', border: '1px solid var(--sec-border)' }}>
                      <MarkdownView content={workflowResults[idx].content} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save Strategy Form */}
      {showForm && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-success)' }}>
          <h2 className="form-section-title">Strategie-Ergebnis speichern</h2>
          <form onSubmit={handleSaveStrategy}>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="form-input"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Zielgruppe *</label>
                <input
                  type="text"
                  value={formData.targetAudience}
                  onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Branche *</label>
                <input
                  type="text"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">ICP (Ideal Customer Profile)</label>
              <textarea
                value={formData.icp || ''}
                onChange={(e) => setFormData({ ...formData, icp: e.target.value })}
                rows={6}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Pain Points <span className="form-label-hint">eine pro Zeile</span></label>
              <textarea
                value={(formData.painPoints || []).join('\n')}
                onChange={(e) => setFormData({ ...formData, painPoints: e.target.value.split('\n').map((l) => l.trim()).filter(Boolean) })}
                rows={5}
                className="form-textarea"
                placeholder="z.B. Fachkräftemangel im SOC"
              />
              <div className="form-helper">Strukturierte Pain Points fließen als Kontext in Beitragsideen und Beiträge ein.</div>
            </div>

            <div className="form-group">
              <label className="form-label">Vollstaendige Analyse</label>
              <textarea
                value={formData.rawAnalysis || ''}
                onChange={(e) => setFormData({ ...formData, rawAnalysis: e.target.value })}
                rows={10}
                className="form-textarea"
              />
            </div>

            <div className="btn-group">
              <button type="submit" disabled={saving} className="btn btn-success">
                {saving ? 'Speichern...' : 'Strategie-Ergebnis speichern'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn btn-secondary">
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Saved Strategies */}
      <div style={{ marginTop: '24px' }}>
        <h2 className="form-section-title">Gespeicherte Strategien ({strategies.length})</h2>
        {strategies.length === 0 ? (
          <EmptyState
            icon="compass"
            title="Noch keine Strategie"
            description="Die Analyse ermittelt Zielgruppe, ICP und Pain Points. Alles Weitere im LinkedIn-Studio baut darauf auf."
            action={{ label: 'Strategie-Analyse starten', onClick: () => setShowWorkflowRunner(true) }}
          />
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {strategies.map((strategy) => (
              <div key={strategy.id} className="card">
                <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === strategy.id ? null : strategy.id)}>
                  <div>
                    <h3 className="card-title">
                      {strategy.name}
                      {strategy.isActive && <span className="badge badge-success" style={{ marginLeft: '8px' }}>Aktiv</span>}
                    </h3>
                    <p className="card-description">
                      <strong>Zielgruppe:</strong> {strategy.targetAudience} | <strong>Branche:</strong> {strategy.industry}
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--sec-fg-subtle)' }}>
                      Erstellt am {new Date(strategy.createdAt).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <span style={{ fontSize: '18px', color: 'var(--sec-fg-subtle)' }}>
                    {expandedId === strategy.id ? '\u25B2' : '\u25BC'}
                  </span>
                </div>

                {expandedId === strategy.id && (
                  <div style={{ marginTop: '16px' }}>
                    {strategy.icp && (
                      <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>ICP (Ideal Customer Profile)</h4>
                        <div style={{ backgroundColor: 'var(--sec-surface-alt)', padding: '12px', borderRadius: '4px', border: '1px solid var(--sec-border)' }}>
                          <MarkdownView content={strategy.icp} />
                        </div>
                      </div>
                    )}

                    {parseJson(strategy.challenges).length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Herausforderungen</h4>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                          {parseJson(strategy.challenges).map((c: string, i: number) => (
                            <li key={i} style={{ fontSize: '13px', marginBottom: '4px' }}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {parseJson(strategy.painPoints).length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Pain Points</h4>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                          {parseJson(strategy.painPoints).map((p: string, i: number) => (
                            <li key={i} style={{ fontSize: '13px', marginBottom: '4px' }}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Referenzprofile verknüpfen</h4>
                      {profiles.length === 0 ? (
                        <p className="text-sm text-secondary">
                          Noch keine Referenzprofile vorhanden. <Link to="/linkedin-profiles">Profile anlegen</Link>, dann hier verknüpfen.
                        </p>
                      ) : (
                        <div className="strategy-profile-list">
                          {profiles.map((p) => (
                            <label key={p.id} className="form-check">
                              <input
                                type="checkbox"
                                checked={parseIds(strategy.linkedInProfileIds).includes(p.id)}
                                onChange={() => handleToggleProfile(strategy, p.id)}
                                disabled={busyId === strategy.id}
                              />
                              {p.name} <span className="text-muted">@{p.username}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {strategy.rawAnalysis && (
                      <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Vollstaendige Analyse</h4>
                        <div style={{ backgroundColor: 'var(--sec-surface-alt)', padding: '12px', borderRadius: '4px', maxHeight: '400px', overflow: 'auto', border: '1px solid var(--sec-border)' }}>
                          <MarkdownView content={strategy.rawAnalysis} />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="card-footer">
                  <div className="btn-group">
                    {!strategy.isActive && (
                      <button onClick={() => handleSetActive(strategy.id)} className="btn btn-primary btn-sm" disabled={busyId === strategy.id}>
                        Als aktive Strategie setzen
                      </button>
                    )}
                    <button onClick={() => setExpandedId(expandedId === strategy.id ? null : strategy.id)} className="btn btn-secondary btn-sm">
                      {expandedId === strategy.id ? 'Details ausblenden' : 'Details & Profile'}
                    </button>
                    <button onClick={() => handleDelete(strategy.id)} className="btn btn-danger btn-sm">
                      Loeschen
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Naechste Schritte */}
      <div className="flow-banner flow-banner--info" style={{ marginTop: '24px' }}>
        <Icon name="arrow-right" size={16} />
        <span>
          <strong>Weiter im Prozess:</strong> Referenzprofile sammeln und mit der aktiven Strategie verknüpfen, dann Serienformate festlegen.
        </span>
        <div className="btn-group" style={{ marginLeft: 'auto' }}>
          <Link to="/linkedin-profiles" className="btn btn-primary btn-sm">2 · Referenzprofile</Link>
          <Link to="/series-formats" className="btn btn-secondary btn-sm">3 · Serienformate</Link>
        </div>
      </div>
    </div>
  );
};

export default StrategyWorkflowPage;
